import { Trial, TrialIngredient } from "../../../types/mes";

/** API/Prisma Decimal često stigne kao string */
export function bakerPctNum(v: unknown): number {
  if (v == null || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

export function sumBakerPct(ingredients: TrialIngredient[]): number {
  return ingredients.reduce((sum, i) => sum + bakerPctNum(i.BakerPercentage), 0);
}

export function getTrialId(t: Trial): number {
  return t.ID;
}

export function getTrialName(t: Trial): string {
  return t.TrialCode || `Pokus ${getTrialId(t)}`;
}

export function formatTrialDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("hr-HR");
}

function ingredientRowsHtml(ingredients: TrialIngredient[], doughMass: number): string {
  const totalPek = sumBakerPct(ingredients);
  const faktor = totalPek > 0 && doughMass > 0 ? doughMass / totalPek : 1;

  return ingredients
    .slice()
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
    .map((i) => {
      const pek = bakerPctNum(i.BakerPercentage);
      const kg = (pek * faktor).toFixed(3);
      const isNew = i.IsNewIngredient ? "⚠️ NOVA" : "";
      return `
          <tr>
            <td>${i.IngredientName} ${isNew}</td>
            <td>${i.IngredientType || ""}</td>
            <td>${pek.toFixed(3)}</td>
            <td>${kg}</td>
            <td></td>
            <td>${i.Notes || ""}</td>
          </tr>
        `;
    })
    .join("");
}

function workOrderBodyHtml(trial: Trial, ingredients: TrialIngredient[]): string {
  const doughMass = bakerPctNum(trial.DoughMassKg);
  const ingredientRows = ingredientRowsHtml(ingredients, doughMass);
  const productName = trial.Product_ProductToTrial?.ProductName || "-";

  return `
  <h2>🧪 Radni nalog probe - ${trial.TrialCode}</h2>
  <div class="meta">
    Datum: <b>${formatTrialDate(trial.TrialDate)}</b> |
    Proizvod: <b>${productName}</b> |
    Masa zamjesa: <b>${doughMass.toFixed(2)} kg</b>
  </div>
  <div class="purpose">🎯 Svrha: ${trial.Purpose || "-"}</div>
  ${trial.Notes ? `<div class="meta">Napomena: ${trial.Notes}</div>` : ""}

  <div class="section">Receptura</div>
  <table>
    <tr>
      <th style="width:30%">Sirovina</th>
      <th style="width:12%">Tip</th>
      <th style="width:10%">PEK %</th>
      <th style="width:12%">Kg / zamjes</th>
      <th style="width:18%">LOT</th>
      <th style="width:18%">Napomena</th>
    </tr>
    ${ingredientRows}
  </table>

  <div class="section" style="margin-top:24px">Pracenje - Mikser</div>
  <table class="tracking">
    <tr>
      <th>Batch ID</th><th>Start</th><th>Kraj mij.</th><th>Kraj mase</th>
      <th>Temp (°C)</th><th>pH start.</th><th>pH zamj.</th><th>pH Rheon</th>
      <th>Odstupanja</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>
  </table>

  <div class="section" style="margin-top:16px">Pracenje - Fermentacija</div>
  <table class="tracking">
    <tr>
      <th>Batch ID</th><th>Vis. mjerica (Mikser)</th><th>Vis. mjerica (Rheon)</th><th>Napomena</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td></tr>
  </table>

  <div class="section" style="margin-top:16px">Pracenje - Pecenje</div>
  <table class="tracking">
    <tr>
      <th>Batch ID</th><th>Start</th><th>Kraj</th><th>Temp (°C)</th><th>Vis. mjerica</th><th>Napomena</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td><td></td><td></td></tr>
  </table>

  <div class="section" style="margin-top:16px">Pracenje - Kvaliteta</div>
  <table class="tracking">
    <tr>
      <th>Batch ID</th><th>Spec. vol (L/kg)</th><th>Skart (kom)</th>
      <th>Skart boja</th><th>Skart oblik</th><th>Napomena</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td><td></td><td></td></tr>
  </table>`;
}

const PRINT_STYLES = `
    body { font-family: Arial, sans-serif; font-size: 11pt; margin: 15mm; }
    h2 { margin-bottom: 4px; }
    .meta { color: #555; margin-bottom: 10px; font-size: 10pt; }
    .purpose { background: #fff9e6; border-left: 4px solid #f39c12; padding: 6px 10px; margin-bottom: 12px; font-size: 10pt; }
    table { border-collapse: collapse; width: 100%; margin-top: 10px; }
    th { background: #2c3e50; color: white; padding: 6px 8px; text-align: left; font-size: 10pt; }
    td { border: 1px solid #ccc; padding: 5px 8px; font-size: 10pt; }
    tr:nth-child(even) { background: #f9f9f9; }
    .section { margin-top: 18px; font-weight: bold; font-size: 11pt; border-bottom: 1px solid #ccc; padding-bottom: 3px; }
    .tracking th { background: #1a5276; }
    .page-break { page-break-before: always; }
    @media print { body { margin: 10mm; } .page-break { page-break-before: always; } }
`;

function workOrderHtml(trial: Trial, ingredients: TrialIngredient[]): string {
  return `<!DOCTYPE html>
<html lang="hr">
<head>
  <meta charset="UTF-8">
  <title>Radni nalog probe - ${trial.TrialCode}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
${workOrderBodyHtml(trial, ingredients)}
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;
}

export function printWorkOrder(trial: Trial, ingredients: TrialIngredient[]): boolean {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;
  printWindow.document.open();
  printWindow.document.write(workOrderHtml(trial, ingredients));
  printWindow.document.close();
  return true;
}

export function printBulkWorkOrders(
  trials: { trial: Trial; ingredients: TrialIngredient[] }[],
  date: Date
): boolean {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;

  const pages = trials
    .map(({ trial, ingredients }, idx) => {
      const wrap = idx > 0 ? ' class="page-break"' : "";
      return `<div${wrap}>${workOrderBodyHtml(trial, ingredients)}</div>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html lang="hr">
<head>
  <meta charset="UTF-8">
  <title>Radni nalozi - ${date.toLocaleDateString("hr-HR")}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
  <div style="text-align: center; margin-bottom: 15px;">
    <h1 style="margin: 0;">Radni nalozi proba - ${date.toLocaleDateString("hr-HR")}</h1>
    <p style="color: #666; margin: 4px 0;">${trials.length} proba</p>
  </div>
  ${pages}
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}
